import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loader, Button, SectionCard } from "@/components/PageUI";
import { toast } from "sonner";
import { UserPlus, Check, Copy, Link2 } from "lucide-react";

export default function Registrations() {
  const { club } = useAuth();
  const [regs, setRegs] = useState(null);

  const load = () => api.get("/registrations").then((r) => setRegs(r.data)).catch(() => setRegs([]));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.put(`/registrations/${id}/approve`); toast.success("Approved & added as athlete"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const link = `${window.location.origin}/join/${club?.id}`;
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Registration link copied"); };

  if (!regs) return <Loader />;
  const pending = regs.filter((r) => r.status === "pending");

  return (
    <div className="animate-fade-up">
      <PageHeader title="Registrations" subtitle="Parents register athletes online. Approve to add them to a team." testid="registrations-header" />

      <SectionCard title="Public registration link" className="mb-5" testid="reg-link-card"
        action={<Button size="sm" variant="outline" onClick={copy} data-testid="copy-reg-link"><Copy size={14} className="mr-1.5" /> Copy</Button>}>
        <div className="flex items-center gap-2 text-sm bg-accent rounded-lg px-3 py-2">
          <Link2 size={15} className="text-secondary shrink-0" />
          <span className="truncate font-mono text-xs">{link}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Share this with parents to register players, upload a photo, accept the waiver and pay online.</p>
      </SectionCard>

      {pending.length === 0 ? (
        <EmptyState icon={UserPlus} title="No pending registrations" desc="New parent registrations will appear here for approval." testid="empty-registrations" />
      ) : (
        <div className="space-y-3">
          {pending.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border bg-card p-5 flex items-center justify-between gap-4" data-testid={`reg-${r.id}`}>
              <div className="flex items-center gap-4">
                {r.photo ? <img src={r.photo} alt="" className="h-12 w-12 rounded-full object-cover" /> : <div className="h-12 w-12 rounded-full brand-gradient flex items-center justify-center text-white font-semibold">{r.player_name[0]}</div>}
                <div>
                  <p className="font-semibold">{r.player_name}</p>
                  <p className="text-sm text-muted-foreground">Parent: {r.parent_name} · {r.parent_email}</p>
                  {r.medical_info && <p className="text-xs text-red-400 mt-0.5">Medical: {r.medical_info}</p>}
                </div>
              </div>
              <Button size="sm" onClick={() => approve(r.id)} className="brand-gradient text-white border-0" data-testid={`approve-reg-${r.id}`}><Check size={15} className="mr-1.5" /> Approve</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
